import * as THREE from 'three';
import { Enemy, EnemyType } from './enemies';
import { GameWorld } from './world';
import { wrappedAngle } from './utils';
import { loadSettings } from './storage';

type LitMaterial = THREE.MeshLambertMaterial;
type DetailMode = 'full' | 'lite';

interface ComposerLike {
  render(): void;
  setSize(width: number, height: number): void;
  setPixelRatio(ratio: number): void;
  setBloomEnabled(enabled: boolean): void;
}

type EnemyParts = {
  wings?: THREE.Object3D[];
  tentacles?: THREE.Object3D[];
  petals?: THREE.Object3D[];
  crown?: THREE.Object3D[];
  legs?: THREE.Object3D[];
  pods?: THREE.Object3D[];
  core?: THREE.Object3D;
};

type EnemyVisual = {
  type: EnemyType;
  detail: DetailMode;
  group: THREE.Group;
  materials: LitMaterial[];
  seed: number;
};

type PlayerVisual = {
  group: THREE.Group;
  shell: THREE.Mesh<THREE.DodecahedronGeometry, LitMaterial>;
  core: THREE.Mesh<THREE.IcosahedronGeometry, LitMaterial>;
  fins: THREE.Mesh<THREE.BoxGeometry, LitMaterial>[];
};

const BASE_CLEAR_COLOR = 0x070b16;
const PLAYER_BASE_RADIUS = 15;
const REDUCED_DETAIL_ENTER_THRESHOLD = 24;
const REDUCED_DETAIL_EXIT_THRESHOLD = 16;
const REDUCED_PIXEL_RATIO_THRESHOLD = 18;
const HEAVY_PIXEL_RATIO_THRESHOLD = 30;
const BASE_RADII: Record<EnemyType, number> = {
  swarmer: 10,
  drifter: 20,
  titan: 40,
  overlord: 55,
  spitter: 16,
  splitter: 24,
  bomber: 14,
  boss: 68,
  stalker: 13,
  sentinel: 28,
  lancer: 18,
};

const GEOMETRY = {
  playerShell: new THREE.DodecahedronGeometry(15, 0),
  playerCore: new THREE.IcosahedronGeometry(9, 0),
  playerFin: new THREE.BoxGeometry(5.5, 16, 1.8),
  swarmerThorax: new THREE.DodecahedronGeometry(7.8, 0),
  swarmerAbdomen: new THREE.OctahedronGeometry(6.2, 0),
  swarmerHead: new THREE.OctahedronGeometry(4.1, 0),
  swarmerWing: new THREE.BoxGeometry(8.8, 1.1, 5.8),
  swarmerStinger: new THREE.ConeGeometry(1.8, 6.5, 4),
  swarmerLeg: new THREE.ConeGeometry(1.2, 7, 4),
  drifterMantle: new THREE.IcosahedronGeometry(18, 0),
  drifterCore: new THREE.OctahedronGeometry(6.4, 0),
  drifterTentacle: new THREE.CylinderGeometry(1.2, 2.9, 18, 5),
  drifterFrill: new THREE.ConeGeometry(2.8, 10, 4),
  titanHull: new THREE.IcosahedronGeometry(31, 0),
  titanCrust: new THREE.DodecahedronGeometry(11, 0),
  titanSpire: new THREE.ConeGeometry(5.2, 18, 4),
  titanCore: new THREE.OctahedronGeometry(8.5, 0),
  overlordThorax: new THREE.CylinderGeometry(16, 20, 13, 6),
  overlordAbdomen: new THREE.DodecahedronGeometry(15, 0),
  overlordWing: new THREE.BoxGeometry(10, 25, 3.5),
  overlordHorn: new THREE.ConeGeometry(3.5, 12, 4),
  overlordPod: new THREE.DodecahedronGeometry(5.5, 0),
  overlordCore: new THREE.OctahedronGeometry(9, 0),
  spitterBody: new THREE.IcosahedronGeometry(12, 0),
  spitterMaw: new THREE.ConeGeometry(4.2, 7.5, 6),
  spitterWart: new THREE.DodecahedronGeometry(1.7, 0),
  splitterMembrane: new THREE.SphereGeometry(17, 7, 5),
  splitterNucleus: new THREE.OctahedronGeometry(4.6, 0),
  bomberHull: new THREE.OctahedronGeometry(10, 0),
  bomberFin: new THREE.ConeGeometry(1.8, 5.5, 4),
  bossShell: new THREE.IcosahedronGeometry(34, 0),
  bossPlate: new THREE.BoxGeometry(16, 3.2, 5.5),
  bossEye: new THREE.SphereGeometry(9.5, 8, 6),
  bossSatellite: new THREE.OctahedronGeometry(4.4, 0),
};

function makeMaterial(color: number, emissive = color): LitMaterial {
  const mat = new THREE.MeshLambertMaterial({
    color: vivid(color, 1.55, 0.14),
    emissive: vivid(emissive, 1.1, 0.06),
    emissiveIntensity: 0.5,
    flatShading: true,
  });
  return mat;
}

/** Push a hex color toward vivid neon: saturation & lightness lift. */
function vivid(hex: number, satBoost: number, lightBoost: number): THREE.Color {
  const c = new THREE.Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  c.setHSL(
    hsl.h,
    Math.min(1, hsl.s * satBoost + 0.15),
    Math.min(0.62, hsl.l * 1.35 + lightBoost),
  );
  return c;
}

function varyMaterial(material: LitMaterial, hueShift: number, lightnessShift: number): void {
  material.color.offsetHSL(hueShift, 0, lightnessShift);
  material.emissive.copy(material.color).multiplyScalar(0.5);
}

function createMesh<TGeometry extends THREE.BufferGeometry>(
  geometry: TGeometry,
  material: LitMaterial,
): THREE.Mesh<TGeometry, LitMaterial> {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.matrixAutoUpdate = true;
  return mesh;
}

function isMobileLikeViewport(): boolean {
  return window.innerWidth < 900 || window.matchMedia('(pointer: coarse)').matches;
}

function createEnemyPool(): Record<EnemyType, EnemyVisual[]> {
  return {
    swarmer: [],
    drifter: [],
    titan: [],
    overlord: [],
    spitter: [],
    splitter: [],
    bomber: [],
    boss: [],
    stalker: [],
    sentinel: [],
    lancer: [],
  };
}

export class ThreeEntityRenderer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
  private readonly playerVisual: PlayerVisual;
  private readonly enemyVisuals = new Map<Enemy, EnemyVisual>();
  private readonly enemyPools: Record<DetailMode, Record<EnemyType, EnemyVisual[]>> = {
    full: createEnemyPool(),
    lite: createEnemyPool(),
  };
  private composer: ComposerLike | null = null;
  private composerReady = false;
  private slowFrames = 0;
  private readonly basePixelRatioCap: number;
  private currentDevicePixelRatio = 1;
  private currentPixelRatio = 1;
  private detailMode: DetailMode = 'full';
  private width = 1;
  private height = 1;

  constructor(overlayCanvas: HTMLCanvasElement) {
    const compactQuality = isMobileLikeViewport();
    this.basePixelRatioCap = compactQuality ? 1.35 : 1.85;

    this.renderer = new THREE.WebGLRenderer({
      antialias: !compactQuality,
      alpha: false,
      powerPreference: 'high-performance',
      precision: 'mediump',
      stencil: false,
      depth: true,
    });
    this.renderer.setClearColor(BASE_CLEAR_COLOR, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.id = 'playfield-3d';
    this.renderer.domElement.style.position = 'fixed';
    this.renderer.domElement.style.inset = '0';
    this.renderer.domElement.style.width = '100vw';
    this.renderer.domElement.style.height = '100vh';
    this.renderer.domElement.style.zIndex = '0';
    this.renderer.domElement.style.pointerEvents = 'none';

    overlayCanvas.style.position = 'fixed';
    overlayCanvas.style.inset = '0';
    overlayCanvas.style.zIndex = '1';
    overlayCanvas.style.background = 'transparent';

    const parent = overlayCanvas.parentElement ?? document.body;
    parent.insertBefore(this.renderer.domElement, overlayCanvas);

    this.scene.background = this.createGradientBackdrop();
    this.scene.add(new THREE.AmbientLight(0x8aa5ff, 1.35));

    const hemi = new THREE.HemisphereLight(0x9ed0ff, 0x11151b, 1.0);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight(0xe0eeff, 2.1);
    key.position.set(-0.5, 0.8, 1.3);
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xffc695, 0.7);
    fill.position.set(0.85, -0.5, 1);
    this.scene.add(fill);

    // Rim/back light — cool edge separation so silhouettes pop off the dark.
    const rim = new THREE.DirectionalLight(0x5f7fff, 1.6);
    rim.position.set(0.2, -0.4, -1.2);
    this.scene.add(rim);

    this.createDustField();
    this.playerVisual = this.createPlayerVisual();
    this.scene.add(this.playerVisual.group);

    void this.initComposer(compactQuality);
  }

  /** Post-processing (bloom) is loaded lazily and degrades gracefully. */
  private async initComposer(compactQuality: boolean): Promise<void> {
    try {
      const [{ EffectComposer }, { RenderPass }, { UnrealBloomPass }, { OutputPass }] = await Promise.all([
        import('three/examples/jsm/postprocessing/EffectComposer.js'),
        import('three/examples/jsm/postprocessing/RenderPass.js'),
        import('three/examples/jsm/postprocessing/UnrealBloomPass.js'),
        import('three/examples/jsm/postprocessing/OutputPass.js'),
      ]);

      const composer = new EffectComposer(this.renderer);
      composer.addPass(new RenderPass(this.scene, this.camera));
      // Half-res bloom on compact devices keeps the glow cheap.
      const bloomRes = compactQuality
        ? new THREE.Vector2(Math.max(2, Math.floor(this.width / 2)), Math.max(2, Math.floor(this.height / 2)))
        : new THREE.Vector2(this.width, this.height);
      const bloom = new UnrealBloomPass(bloomRes, compactQuality ? 0.7 : 0.85, 0.55, 0.42);
      composer.addPass(bloom);
      composer.addPass(new OutputPass());
      composer.setSize(this.width, this.height);
      composer.setPixelRatio(this.currentPixelRatio);

      this.composer = {
        render: () => composer.render(),
        setSize: (w, h) => composer.setSize(w, h),
        setPixelRatio: (r) => composer.setPixelRatio(r),
        setBloomEnabled: (enabled) => { bloom.enabled = enabled; },
      };
      this.composerReady = true;
    } catch {
      this.composer = null;
      this.composerReady = true;
    }
  }

  /** Deep-space gradient backdrop rendered behind everything. */
  private createGradientBackdrop(): THREE.Texture | THREE.Color {
    try {
      const cv = document.createElement('canvas');
      cv.width = 32;
      cv.height = 256;
      const g = cv.getContext('2d');
      if (!g) return new THREE.Color(BASE_CLEAR_COLOR);
      const grad = g.createLinearGradient(0, 0, 0, 256);
      grad.addColorStop(0, '#0b1030');
      grad.addColorStop(0.45, '#090d24');
      grad.addColorStop(1, '#05070f');
      g.fillStyle = grad;
      g.fillRect(0, 0, 32, 256);
      const tex = new THREE.CanvasTexture(cv);
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    } catch {
      return new THREE.Color(BASE_CLEAR_COLOR);
    }
  }

  /** Additive drifting dust motes for parallax depth. */
  private createDustField(): void {
    try {
      const count = 160;
      const positions = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 3200;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
        positions[i * 3 + 2] = -60 - Math.random() * 220;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: 0x7fb4ff,
        size: 5,
        sizeAttenuation: false,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const points = new THREE.Points(geo, mat);
      points.userData.drift = true;
      this.scene.add(points);
      this.dustPoints = points;
    } catch {
      // Dust is decorative; ignore failures.
    }
  }

  private dustPoints: THREE.Points | null = null;

  resize(width: number, height: number, dpr: number): void {
    this.width = width;
    this.height = height;
    this.currentDevicePixelRatio = dpr;
    this.camera.left = -width / 2;
    this.camera.right = width / 2;
    this.camera.top = height / 2;
    this.camera.bottom = -height / 2;
    this.camera.position.set(0, 0, 400);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();

    this.updatePixelRatio(1);
    this.renderer.setSize(width, height, false);
    this.composer?.setSize(width, height);
  }

  /** Release all pooled visuals and hide player — called on run reset / quit. */
  clear(): void {
    for (const visual of this.enemyVisuals.values()) {
      visual.group.visible = false;
      this.enemyPools[visual.detail][visual.type].push(visual);
    }
    this.enemyVisuals.clear();
    this.playerVisual.group.visible = false;
  }

  /** Fully dispose WebGL resources (call on page unload or WebGL context loss). */
  dispose(): void {
    try {
      this.clear();
      this.renderer.dispose();
      const el = this.renderer.domElement;
      el.parentElement?.removeChild(el);
      for (const pool of Object.values(this.enemyPools)) {
        for (const list of Object.values(pool)) list.length = 0;
      }
      this.scene.clear();
    } catch {
      // best-effort cleanup
    }
  }

  render(world: GameWorld | null, time: number): void {
    this.applyAdaptiveQuality(world?.spawner.enemies.length ?? 0);

    if (this.dustPoints) {
      let dustMul = 1;
      try {
        const s = loadSettings();
        if (s.reducedMotion) dustMul = 0.25;
        else if (s.particleQuality === 'low') dustMul = 0.5;
      } catch {
        // ignore
      }
      this.dustPoints.rotation.z = time * 0.008 * dustMul;
      this.dustPoints.position.x = Math.sin(time * 0.05 * dustMul) * 30;
      this.dustPoints.position.y = Math.cos(time * 0.04 * dustMul) * 20;
      this.dustPoints.visible = loadSettings().particleQuality !== 'low' || (world?.spawner.enemies.length ?? 0) < 20;
    }

    if (!world) {
      this.playerVisual.group.visible = false;
      for (const visual of this.enemyVisuals.values()) {
        visual.group.visible = false;
      }
      this.renderFrame();
      return;
    }

    this.syncPlayer(world, time);
    this.syncEnemies(world, time);
    this.renderFrame();
  }

  private renderFrame(): void {
    if (this.composer) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  private syncPlayer(world: GameWorld, time: number): void {
    const player = world.player;
    const screen = world.camera.worldToScreen(player.x, player.y);
    const root = this.playerVisual.group;
    root.visible = true;
    root.position.set(screen.x - this.width / 2, this.height / 2 - screen.y, 10);
    root.scale.setScalar(player.radius / PLAYER_BASE_RADIUS);
    root.rotation.z = time * 0.18;

    const pulse = 1 + Math.sin(time * 2.8) * 0.08;
    this.playerVisual.core.rotation.y = time * 1.4;
    this.playerVisual.core.rotation.x = time * 0.8;
    this.playerVisual.core.scale.setScalar(pulse);

    for (let i = 0; i < this.playerVisual.fins.length; i++) {
      const fin = this.playerVisual.fins[i];
      fin.rotation.z = (i / this.playerVisual.fins.length) * Math.PI * 2 + time * 0.42;
      fin.rotation.x = 0.34 + Math.sin(time * 2 + i) * 0.16;
    }

    this.playerVisual.shell.material.emissiveIntensity = 0.55 + player.hurtRatio * 0.65;
    this.playerVisual.core.material.emissiveIntensity = 0.85 + player.hurtRatio * 0.55;
  }

  private syncEnemies(world: GameWorld, time: number): void {
    const active = new Set(world.spawner.enemies);

    for (const [enemy, visual] of this.enemyVisuals) {
      if (!active.has(enemy)) {
        this.releaseEnemyVisual(enemy, visual);
      }
    }

    for (const enemy of world.spawner.enemies) {
      let visual = this.enemyVisuals.get(enemy);
      if (!visual) {
        visual = this.acquireEnemyVisual(enemy.type);
        this.enemyVisuals.set(enemy, visual);
      }
      this.applyStageTint(visual, enemy);
      this.updateEnemyVisual(enemy, visual, world, time);
    }
  }

  /** Boss bodies shift hue per Mk stage so each stage reads as a new foe. */
  private applyStageTint(visual: EnemyVisual, enemy: Enemy): void {
    if (!enemy.isBoss) return;
    const tintFor = (visual.group.userData.tintFor as number | undefined) ?? -1;
    if (tintFor === enemy.stage) return;
    const hueShift = ((enemy.stage - 1) % 5) * 0.078;
    for (const material of visual.materials) {
      material.color.offsetHSL(hueShift, 0, 0);
      material.emissive.copy(material.color).multiplyScalar(0.5);
    }
    visual.group.userData.tintFor = enemy.stage;
  }

  private acquireEnemyVisual(type: EnemyType): EnemyVisual {
    const pooled = this.enemyPools[this.detailMode][type].pop();
    if (pooled) {
      pooled.group.visible = true;
      return pooled;
    }

    const visual = this.createEnemyVisual(type, this.detailMode);
    this.scene.add(visual.group);
    return visual;
  }

  private releaseEnemyVisual(enemy: Enemy, visual: EnemyVisual): void {
    visual.group.visible = false;
    this.enemyVisuals.delete(enemy);
    this.enemyPools[visual.detail][visual.type].push(visual);
  }

  private updateEnemyVisual(enemy: Enemy, visual: EnemyVisual, world: GameWorld, time: number): void {
    const visible = world.camera.isVisible(enemy.x, enemy.y, enemy.radius + 120);
    visual.group.visible = visible;
    if (!visible) return;

    const screen = world.camera.worldToScreen(enemy.x, enemy.y);
    visual.group.position.set(screen.x - this.width / 2, this.height / 2 - screen.y, 0);
    visual.group.scale.setScalar(enemy.radius / BASE_RADII[enemy.type]);

    const seed = visual.seed;
    const bob = Math.sin(time * 1.8 + seed * 6.1) * 2.4;
    const wobble = Math.sin(time * 2.4 + seed * 4.7) * 0.1;
    const chaseRotation = -wrappedAngle(enemy.x, enemy.y, world.player.x, world.player.y) + Math.PI / 2;
    const baseRotation = enemy.type === 'swarmer'
      ? chaseRotation
      : time * (enemy.type === 'drifter' ? 0.56 : enemy.type === 'titan' ? 0.22 : 0.3) + seed * Math.PI;
    visual.group.position.z = enemy.type === 'overlord' ? 4 + bob * 0.3 : bob;
    visual.group.rotation.z = baseRotation;
    visual.group.rotation.x = enemy.type === 'overlord' ? 0.12 : 0.18 + wobble * 0.22;
    visual.group.rotation.y = enemy.type === 'titan' ? 0.12 + wobble * 0.18 : wobble * 0.14;

    const lowHealth = 1 - enemy.hp / enemy.maxHp;
    let emissiveBoost = 0;
    if (enemy.type === 'bomber' && enemy.fuseRatio > 0) {
      emissiveBoost = enemy.fuseRatio * (Math.sin(time * 30) > 0 ? 0.9 : 0.1);
    }
    if (enemy.isBoss) {
      emissiveBoost = 0.45 + (enemy.bossPhase - 1) * 0.22 + lowHealth * 0.2;
    }
    for (const material of visual.materials) {
      material.emissiveIntensity = 0.42 + lowHealth * 0.28 + emissiveBoost;
    }

    const parts = visual.group.userData as EnemyParts;

    if (parts.wings) {
      for (let i = 0; i < parts.wings.length; i++) {
        parts.wings[i].rotation.x = 0.32 + Math.sin(time * 8 + seed * 5 + i) * 0.2;
      }
    }
    if (parts.legs) {
      for (let i = 0; i < parts.legs.length; i++) {
        parts.legs[i].rotation.z = parts.legs[i].userData.baseRotation + Math.sin(time * 6 + seed * 7 + i) * 0.18;
      }
    }
    if (parts.tentacles) {
      for (let i = 0; i < parts.tentacles.length; i++) {
        parts.tentacles[i].rotation.z = parts.tentacles[i].userData.baseRotation + Math.sin(time * 3 + seed * 3 + i) * 0.28;
        parts.tentacles[i].rotation.x = 0.26 + Math.sin(time * 2.2 + i + seed) * 0.12;
      }
    }
    if (parts.petals) {
      for (let i = 0; i < parts.petals.length; i++) {
        parts.petals[i].rotation.z = (i / parts.petals.length) * Math.PI * 2 + time * 0.28 + seed;
        parts.petals[i].rotation.x = 0.62 + Math.sin(time * 1.6 + i + seed) * 0.12;
      }
    }
    if (parts.crown) {
      for (let i = 0; i < parts.crown.length; i++) {
        parts.crown[i].rotation.z = parts.crown[i].userData.baseRotation + time * 0.16;
      }
    }
    if (parts.pods) {
      for (let i = 0; i < parts.pods.length; i++) {
        parts.pods[i].position.y = parts.pods[i].userData.baseY + Math.sin(time * 2 + seed * 4 + i) * 1.8;
      }
    }
    if (parts.core) {
      const pulse = 0.94 + Math.sin(time * 2.4 + seed * 6.3) * 0.09;
      parts.core.scale.setScalar(pulse);
    }
  }

  private createPlayerVisual(): PlayerVisual {
    const root = new THREE.Group();

    const shell = createMesh(GEOMETRY.playerShell, makeMaterial(0x2f6dff, 0x5ab8ff));
    shell.scale.set(1.08, 0.95, 0.8);
    root.add(shell);

    const core = createMesh(GEOMETRY.playerCore, makeMaterial(0xafe3ff, 0xbfeaff));
    root.add(core);

    const fins: THREE.Mesh<THREE.BoxGeometry, LitMaterial>[] = [];
    for (let i = 0; i < 3; i++) {
      const fin = createMesh(GEOMETRY.playerFin, makeMaterial(0x5e9dff, 0x86d4ff));
      fin.position.set(0, 0, -3.5);
      root.add(fin);
      fins.push(fin);
    }

    return { group: root, shell, core, fins };
  }

  private applyAdaptiveQuality(enemyCount: number): void {
    // User quality pref biases the thresholds
    let qualityBias = 0;
    try {
      const q = loadSettings().particleQuality;
      if (q === 'low') qualityBias = -8;
      else if (q === 'medium') qualityBias = -4;
      if (loadSettings().reducedMotion) qualityBias -= 4;
    } catch {
      // ignore
    }
    const enterThreshold = REDUCED_DETAIL_ENTER_THRESHOLD + qualityBias;
    const exitThreshold = REDUCED_DETAIL_EXIT_THRESHOLD + qualityBias;
    const nextDetailMode: DetailMode = this.detailMode === 'full'
      ? (enemyCount >= enterThreshold ? 'lite' : 'full')
      : (enemyCount <= exitThreshold ? 'full' : 'lite');
    if (nextDetailMode !== this.detailMode) {
      this.detailMode = nextDetailMode;
      this.recycleActiveEnemyVisuals();
    }

    let pixelRatioScale = enemyCount >= HEAVY_PIXEL_RATIO_THRESHOLD
      ? 0.62
      : enemyCount >= REDUCED_PIXEL_RATIO_THRESHOLD
        ? 0.8
        : 1;
    try {
      const q = loadSettings().particleQuality;
      if (q === 'low') pixelRatioScale *= 0.75;
      else if (q === 'medium') pixelRatioScale *= 0.88;
      if (loadSettings().reducedMotion) pixelRatioScale *= 0.85;
    } catch {
      // ignore
    }
    this.updatePixelRatio(pixelRatioScale);

    // Sustained slow frames: drop bloom before anything else.
    if (this.composer && this.composerReady) {
      if (this.lastFrameCost > 24) {
        this.slowFrames++;
        if (this.slowFrames > 90) {
          this.composer.setBloomEnabled(false);
          this.slowFrames = -1000000; // never re-enable this session
        }
      } else if (this.slowFrames > 0) {
        this.slowFrames--;
      }
    }
  }

  private lastFrameCost = 0;

  /** Called by the runtime loop wrapper to report frame pacing. */
  reportFrameCost(ms: number): void {
    this.lastFrameCost = this.lastFrameCost * 0.9 + ms * 0.1;
  }

  private updatePixelRatio(scale: number): void {
    const target = Math.min(this.currentDevicePixelRatio, this.basePixelRatioCap * scale);
    if (Math.abs(target - this.currentPixelRatio) < 0.02) return;
    this.currentPixelRatio = target;
    this.renderer.setPixelRatio(target);
    this.renderer.setSize(this.width, this.height, false);
    this.composer?.setPixelRatio(target);
    this.composer?.setSize(this.width, this.height);
  }

  private recycleActiveEnemyVisuals(): void {
    const activeVisuals = Array.from(this.enemyVisuals.entries());
    this.enemyVisuals.clear();
    for (const [, visual] of activeVisuals) {
      visual.group.visible = false;
      this.enemyPools[visual.detail][visual.type].push(visual);
    }
  }

  private createEnemyVisual(type: EnemyType, detail: DetailMode): EnemyVisual {
    const seed = Math.random();
    switch (type) {
      case 'swarmer':
        return detail === 'lite' ? this.createLiteSwarmerVisual(seed) : this.createSwarmerVisual(seed);
      case 'drifter':
        return detail === 'lite' ? this.createLiteDrifterVisual(seed) : this.createDrifterVisual(seed);
      case 'titan':
        return detail === 'lite' ? this.createLiteTitanVisual(seed) : this.createTitanVisual(seed);
      case 'overlord':
        return detail === 'lite' ? this.createLiteOverlordVisual(seed) : this.createOverlordVisual(seed);
      case 'spitter':
        return detail === 'lite' ? this.createLiteSpitterVisual(seed) : this.createSpitterVisual(seed);
      case 'splitter':
        return detail === 'lite' ? this.createLiteSplitterVisual(seed) : this.createSplitterVisual(seed);
      case 'bomber':
        return this.createBomberVisual(seed);
      case 'stalker':
      case 'sentinel':
      case 'lancer': {
        const group = new THREE.Group();
        const color = type === 'stalker' ? 0x4ae6ff : type === 'sentinel' ? 0xffcd70 : 0x968cff;
        const material = makeMaterial(color);
        const core = createMesh(GEOMETRY.bomberHull, material);
        core.scale.setScalar(type === 'sentinel' ? 1.6 : 0.8);
        group.add(core);
        const count = type === 'sentinel' ? 6 : type === 'stalker' ? 3 : 2;
        for (let i = 0; i < count; i++) {
          const fin = createMesh(GEOMETRY.drifterFrill, material);
          const angle = i * Math.PI * 2 / count;
          const radius = type === 'sentinel' ? 22 : 12;
          fin.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
          fin.rotation.z = angle - Math.PI / 2;
          group.add(fin);
        }
        return { type, detail, group, materials: [material], seed };
      }
      case 'boss':
        return this.createBossVisual(seed);
    }
  }

  private createSpitterVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const bodyMat = makeMaterial(0x2f7a4c, 0x54ff96);
    varyMaterial(bodyMat, (seed - 0.5) * 0.05, 0.02);
    const body = createMesh(GEOMETRY.spitterBody, bodyMat);
    body.scale.set(1.1, 0.9, 0.85);
    root.add(body);
    materials.push(bodyMat);

    const mawMat = makeMaterial(0xd8ffe8, 0xbaffd6);
    const maw = createMesh(GEOMETRY.spitterMaw, mawMat);
    maw.position.set(0, -10, 3);
    maw.rotation.x = Math.PI / 2.4;
    root.add(maw);
    materials.push(mawMat);

    const wartMat = makeMaterial(0x1f5236, 0x3fd97e);
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2 + seed;
      const wart = createMesh(GEOMETRY.spitterWart, wartMat);
      wart.position.set(Math.cos(angle) * 10, Math.sin(angle) * 7, -4);
      root.add(wart);
    }
    materials.push(wartMat);

    root.userData = { core: maw };
    return { type: 'spitter', detail: 'full', group: root, materials, seed };
  }

  private createLiteSpitterVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const bodyMat = makeMaterial(0x2f7a4c, 0x54ff96);
    const body = createMesh(GEOMETRY.spitterBody, bodyMat);
    body.scale.set(1.12, 0.92, 0.86);
    root.add(body);
    materials.push(bodyMat);

    const mawMat = makeMaterial(0xd8ffe8, 0xbaffd6);
    const maw = createMesh(GEOMETRY.spitterMaw, mawMat);
    maw.position.set(0, -10, 3);
    maw.rotation.x = Math.PI / 2.4;
    root.add(maw);
    materials.push(mawMat);

    root.userData = {};
    return { type: 'spitter', detail: 'lite', group: root, materials, seed };
  }

  private createSplitterVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const membraneMat = makeMaterial(0x7a2f66, 0xff5fdc);
    membraneMat.transparent = true;
    membraneMat.opacity = 0.82;
    varyMaterial(membraneMat, (seed - 0.5) * 0.04, 0.02);
    const membrane = createMesh(GEOMETRY.splitterMembrane, membraneMat);
    membrane.scale.set(1.15, 0.95, 0.9);
    root.add(membrane);
    materials.push(membraneMat);

    const nucleusMat = makeMaterial(0xffc2ec, 0xffa0e2);
    const nuclei: THREE.Object3D[] = [];
    for (let i = 0; i < 3; i++) {
      const angle = (i / 3) * Math.PI * 2;
      const nucleus = createMesh(GEOMETRY.splitterNucleus, nucleusMat);
      nucleus.position.set(Math.cos(angle) * 4.5, Math.sin(angle) * 4.5, 4);
      nucleus.userData.baseX = nucleus.position.x;
      nucleus.userData.baseY = nucleus.position.y;
      root.add(nucleus);
      nuclei.push(nucleus);
    }
    materials.push(nucleusMat);

    root.userData = { pods: nuclei };
    return { type: 'splitter', detail: 'full', group: root, materials, seed };
  }

  private createLiteSplitterVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const membraneMat = makeMaterial(0x7a2f66, 0xff5fdc);
    membraneMat.transparent = true;
    membraneMat.opacity = 0.82;
    const membrane = createMesh(GEOMETRY.splitterMembrane, membraneMat);
    membrane.scale.set(1.18, 0.96, 0.9);
    root.add(membrane);
    materials.push(membraneMat);

    const nucleusMat = makeMaterial(0xffc2ec, 0xffa0e2);
    const nucleus = createMesh(GEOMETRY.splitterNucleus, nucleusMat);
    nucleus.position.z = 4;
    root.add(nucleus);
    materials.push(nucleusMat);

    root.userData = {};
    return { type: 'splitter', detail: 'lite', group: root, materials, seed };
  }

  private createBomberVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const hullMat = makeMaterial(0x767d26, 0xd5ff4f);
    varyMaterial(hullMat, (seed - 0.5) * 0.04, 0.02);
    const hull = createMesh(GEOMETRY.bomberHull, hullMat);
    hull.scale.set(0.95, 1.25, 0.85);
    root.add(hull);
    materials.push(hullMat);

    const finMat = makeMaterial(0x4c521a, 0x9fbf35);
    const fins: THREE.Object3D[] = [];
    for (const side of [-1, 1] as const) {
      const fin = createMesh(GEOMETRY.bomberFin, finMat);
      fin.position.set(side * 7, 4, -2);
      fin.rotation.z = side * 0.7;
      root.add(fin);
      fins.push(fin);
    }
    materials.push(finMat);

    root.userData = { wings: fins };
    return { type: 'bomber', detail: 'full', group: root, materials, seed };
  }

  private createBossVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const shellMat = makeMaterial(0x5c1030, 0xff285a);
    varyMaterial(shellMat, (seed - 0.5) * 0.03, 0.01);
    const shell = createMesh(GEOMETRY.bossShell, shellMat);
    shell.scale.set(1.25, 1.05, 0.72);
    root.add(shell);
    materials.push(shellMat);

    const plateMat = makeMaterial(0x7e1a3e, 0xff5c80);
    const plates: THREE.Object3D[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const plate = createMesh(GEOMETRY.bossPlate, plateMat);
      plate.position.set(Math.cos(angle) * 42, Math.sin(angle) * 42, 0);
      plate.rotation.z = angle + Math.PI / 2;
      plate.userData.baseRotation = plate.rotation.z;
      root.add(plate);
      plates.push(plate);
    }
    materials.push(plateMat);

    const eyeMat = makeMaterial(0xfff0f4, 0xffb9cc);
    const eye = createMesh(GEOMETRY.bossEye, eyeMat);
    eye.position.z = 14;
    root.add(eye);
    materials.push(eyeMat);

    const satMat = makeMaterial(0xffdce5, 0xff8fae);
    const satellites: THREE.Object3D[] = [];
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const satellite = createMesh(GEOMETRY.bossSatellite, satMat);
      satellite.position.set(Math.cos(angle) * 52, Math.sin(angle) * 52, 6);
      satellite.userData.baseRotation = angle;
      root.add(satellite);
      satellites.push(satellite);
    }
    materials.push(satMat);

    root.userData = { crown: plates, pods: satellites, core: eye };
    return { type: 'boss', detail: 'full', group: root, materials, seed };
  }

  private createSwarmerVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const thoraxMat = makeMaterial(0x8e4c30, 0xff7447);
    varyMaterial(thoraxMat, (seed - 0.5) * 0.06, (seed - 0.5) * 0.08);
    const thorax = createMesh(GEOMETRY.swarmerThorax, thoraxMat);
    thorax.scale.set(1.12, 0.9, 0.82);
    root.add(thorax);
    materials.push(thoraxMat);

    const abdomenMat = makeMaterial(0xc9853b, 0xffb24f);
    varyMaterial(abdomenMat, (seed - 0.5) * 0.05, 0.04);
    const abdomen = createMesh(GEOMETRY.swarmerAbdomen, abdomenMat);
    abdomen.position.set(0, 8.2, -0.8);
    abdomen.scale.set(0.95, 1.2, 0.82);
    root.add(abdomen);
    materials.push(abdomenMat);

    const headMat = makeMaterial(0x51342d, 0xbd6756);
    const head = createMesh(GEOMETRY.swarmerHead, headMat);
    head.position.set(0, -8.8, 1.8);
    head.scale.set(0.85, 1.1, 0.85);
    root.add(head);
    materials.push(headMat);

    const wingMat = makeMaterial(0xd2deeb, 0xc4d8ff);
    wingMat.transparent = true;
    wingMat.opacity = 0.76;
    const wings: THREE.Object3D[] = [];
    for (const side of [-1, 1] as const) {
      const wing = createMesh(GEOMETRY.swarmerWing, wingMat);
      wing.position.set(side * 8.7, -1.8, 1.8);
      wing.rotation.z = side * 0.5;
      root.add(wing);
      wings.push(wing);
    }
    materials.push(wingMat);

    const legMat = makeMaterial(0x3d261d, 0x74453b);
    const legs: THREE.Object3D[] = [];
    for (let i = 0; i < 3; i++) {
      const legAngle = -0.8 + i * 0.8;
      for (const side of [-1, 1] as const) {
        const leg = createMesh(GEOMETRY.swarmerLeg, legMat);
        leg.position.set(side * (5.5 + i * 1.4), i * 2.5 - 1.5, -2.6);
        leg.rotation.x = 1.1;
        leg.rotation.z = side * (1.1 + legAngle * 0.25);
        leg.userData.baseRotation = leg.rotation.z;
        root.add(leg);
        legs.push(leg);
      }
    }
    materials.push(legMat);

    const stingerMat = makeMaterial(0x261615, 0x8f4238);
    const stinger = createMesh(GEOMETRY.swarmerStinger, stingerMat);
    stinger.position.set(0, 15, -0.2);
    stinger.rotation.z = Math.PI;
    root.add(stinger);
    materials.push(stingerMat);

    root.userData = { wings, legs };
    return { type: 'swarmer', detail: 'full', group: root, materials, seed };
  }

  private createDrifterVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const mantleMat = makeMaterial(0x3a6d65, 0x69c7bb);
    varyMaterial(mantleMat, (seed - 0.5) * 0.08, 0.03);
    const mantle = createMesh(GEOMETRY.drifterMantle, mantleMat);
    mantle.scale.set(1.14, 0.84, 0.78);
    root.add(mantle);
    materials.push(mantleMat);

    const coreMat = makeMaterial(0xb6fff7, 0xb8fff1);
    const core = createMesh(GEOMETRY.drifterCore, coreMat);
    core.position.set(0, -1.5, 5.8);
    root.add(core);
    materials.push(coreMat);

    const frillMat = makeMaterial(0x5f928b, 0x9de8dd);
    const petals: THREE.Object3D[] = [];
    for (let i = 0; i < 5; i++) {
      const frill = createMesh(GEOMETRY.drifterFrill, frillMat);
      frill.position.set(0, 2, -4);
      frill.rotation.x = 0.75;
      root.add(frill);
      petals.push(frill);
    }
    materials.push(frillMat);

    const tentacleMat = makeMaterial(0x47655f, 0x88dbce);
    const tentacles: THREE.Object3D[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = -Math.PI * 0.82 + (i / 5) * Math.PI * 1.64;
      const tentacle = createMesh(GEOMETRY.drifterTentacle, tentacleMat);
      tentacle.position.set(Math.cos(angle) * 10.5, 14 + Math.sin(angle) * 2.4, -3.4);
      tentacle.rotation.z = angle + Math.PI;
      tentacle.rotation.x = 0.25;
      tentacle.userData.baseRotation = tentacle.rotation.z;
      root.add(tentacle);
      tentacles.push(tentacle);
    }
    materials.push(tentacleMat);

    root.userData = { tentacles, petals, core };
    return { type: 'drifter', detail: 'full', group: root, materials, seed };
  }

  private createTitanVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const hullMat = makeMaterial(0x5f5a72, 0xa98fff);
    varyMaterial(hullMat, (seed - 0.5) * 0.05, (seed - 0.5) * 0.06);
    const hull = createMesh(GEOMETRY.titanHull, hullMat);
    hull.scale.set(1.08, 0.96, 0.88);
    root.add(hull);
    materials.push(hullMat);

    const coreMat = makeMaterial(0xf7e8ff, 0xf0c2ff);
    const core = createMesh(GEOMETRY.titanCore, coreMat);
    core.position.set(0, -1, 10);
    root.add(core);
    materials.push(coreMat);

    const spireMat = makeMaterial(0x3f3557, 0x9a7ee6);
    const petals: THREE.Object3D[] = [];
    for (let i = 0; i < 7; i++) {
      const spire = createMesh(GEOMETRY.titanSpire, spireMat);
      spire.position.set(0, 0, -7.5);
      spire.rotation.x = 0.56;
      root.add(spire);
      petals.push(spire);
    }
    materials.push(spireMat);

    const crustMat = makeMaterial(0x786489, 0xc6a0ff);
    for (let i = 0; i < 3; i++) {
      const crust = createMesh(GEOMETRY.titanCrust, crustMat);
      crust.position.set((i - 1) * 10, i === 1 ? -8 : 7, 5 - i * 3);
      crust.scale.set(0.65, 0.5, 0.5);
      crust.rotation.set(0.2 * i, 0.3 + i * 0.2, i * 0.4);
      root.add(crust);
    }
    materials.push(crustMat);

    root.userData = { petals, core };
    return { type: 'titan', detail: 'full', group: root, materials, seed };
  }

  private createOverlordVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const thoraxMat = makeMaterial(0x60362a, 0xe25f46);
    varyMaterial(thoraxMat, (seed - 0.5) * 0.03, 0.02);
    const thorax = createMesh(GEOMETRY.overlordThorax, thoraxMat);
    thorax.rotation.z = Math.PI / 6;
    thorax.scale.set(1, 1, 0.7);
    root.add(thorax);
    materials.push(thoraxMat);

    const abdomenMat = makeMaterial(0x9d5c30, 0xff9f51);
    const abdomen = createMesh(GEOMETRY.overlordAbdomen, abdomenMat);
    abdomen.position.set(0, 16, -3);
    abdomen.scale.set(1.1, 1.35, 0.75);
    root.add(abdomen);
    materials.push(abdomenMat);

    const coreMat = makeMaterial(0xffd79e, 0xffba6d);
    const core = createMesh(GEOMETRY.overlordCore, coreMat);
    core.position.set(0, -1, 9.2);
    root.add(core);
    materials.push(coreMat);

    const wingMat = makeMaterial(0x6d4135, 0xc35f4e);
    const wings: THREE.Object3D[] = [];
    for (const side of [-1, 1] as const) {
      const wing = createMesh(GEOMETRY.overlordWing, wingMat);
      wing.position.set(side * 18.5, -1, -1.5);
      wing.rotation.z = side * 0.38;
      wing.rotation.x = 0.32;
      root.add(wing);
      wings.push(wing);
    }
    materials.push(wingMat);

    const hornMat = makeMaterial(0x2a1915, 0x8d3a32);
    const crown: THREE.Object3D[] = [];
    for (let i = 0; i < 5; i++) {
      const horn = createMesh(GEOMETRY.overlordHorn, hornMat);
      horn.position.set(0, -16, 3);
      horn.rotation.x = 0.18;
      horn.userData.baseRotation = (i / 5) * Math.PI * 2;
      horn.rotation.z = horn.userData.baseRotation;
      root.add(horn);
      crown.push(horn);
    }
    materials.push(hornMat);

    const podMat = makeMaterial(0x7e3d2e, 0xff7b50);
    const pods: THREE.Object3D[] = [];
    for (const side of [-1, 1] as const) {
      const pod = createMesh(GEOMETRY.overlordPod, podMat);
      pod.position.set(side * 11, 19, 1);
      pod.scale.set(0.75, 1, 0.72);
      pod.userData.baseY = pod.position.y;
      root.add(pod);
      pods.push(pod);
    }
    materials.push(podMat);

    root.userData = { wings, crown, pods, core };
    return { type: 'overlord', detail: 'full', group: root, materials, seed };
  }

  private createLiteSwarmerVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const thoraxMat = makeMaterial(0x8e4c30, 0xff7447);
    varyMaterial(thoraxMat, (seed - 0.5) * 0.06, (seed - 0.5) * 0.08);
    const thorax = createMesh(GEOMETRY.swarmerThorax, thoraxMat);
    thorax.scale.set(1.16, 0.92, 0.82);
    root.add(thorax);
    materials.push(thoraxMat);

    const abdomenMat = makeMaterial(0xc9853b, 0xffb24f);
    const abdomen = createMesh(GEOMETRY.swarmerAbdomen, abdomenMat);
    abdomen.position.set(0, 8.4, -0.6);
    abdomen.scale.set(0.92, 1.15, 0.82);
    root.add(abdomen);
    materials.push(abdomenMat);

    const wingMat = makeMaterial(0xd2deeb, 0xc4d8ff);
    wingMat.transparent = true;
    wingMat.opacity = 0.64;
    const wings: THREE.Object3D[] = [];
    for (const side of [-1, 1] as const) {
      const wing = createMesh(GEOMETRY.swarmerWing, wingMat);
      wing.position.set(side * 7.6, -0.8, 1.5);
      wing.rotation.z = side * 0.46;
      wing.scale.set(0.82, 0.82, 0.82);
      root.add(wing);
      wings.push(wing);
    }
    materials.push(wingMat);

    root.userData = { wings };
    return { type: 'swarmer', detail: 'lite', group: root, materials, seed };
  }

  private createLiteDrifterVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const mantleMat = makeMaterial(0x3a6d65, 0x69c7bb);
    varyMaterial(mantleMat, (seed - 0.5) * 0.08, 0.03);
    const mantle = createMesh(GEOMETRY.drifterMantle, mantleMat);
    mantle.scale.set(1.08, 0.8, 0.74);
    root.add(mantle);
    materials.push(mantleMat);

    const coreMat = makeMaterial(0xb6fff7, 0xb8fff1);
    const core = createMesh(GEOMETRY.drifterCore, coreMat);
    core.position.set(0, -1.4, 5.2);
    root.add(core);
    materials.push(coreMat);

    const tentacleMat = makeMaterial(0x47655f, 0x88dbce);
    const tentacles: THREE.Object3D[] = [];
    for (let i = 0; i < 3; i++) {
      const angle = -Math.PI * 0.72 + (i / 2) * Math.PI * 1.44;
      const tentacle = createMesh(GEOMETRY.drifterTentacle, tentacleMat);
      tentacle.position.set(Math.cos(angle) * 9.5, 12.5 + Math.sin(angle) * 1.6, -2.8);
      tentacle.rotation.z = angle + Math.PI;
      tentacle.rotation.x = 0.22;
      tentacle.scale.set(0.9, 0.8, 0.9);
      tentacle.userData.baseRotation = tentacle.rotation.z;
      root.add(tentacle);
      tentacles.push(tentacle);
    }
    materials.push(tentacleMat);

    root.userData = { tentacles, core };
    return { type: 'drifter', detail: 'lite', group: root, materials, seed };
  }

  private createLiteTitanVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const hullMat = makeMaterial(0x5f5a72, 0xa98fff);
    varyMaterial(hullMat, (seed - 0.5) * 0.05, (seed - 0.5) * 0.06);
    const hull = createMesh(GEOMETRY.titanHull, hullMat);
    hull.scale.set(1.02, 0.9, 0.82);
    root.add(hull);
    materials.push(hullMat);

    const coreMat = makeMaterial(0xf7e8ff, 0xf0c2ff);
    const core = createMesh(GEOMETRY.titanCore, coreMat);
    core.position.set(0, -1, 8.6);
    root.add(core);
    materials.push(coreMat);

    const petals: THREE.Object3D[] = [];
    const spireMat = makeMaterial(0x3f3557, 0x9a7ee6);
    for (let i = 0; i < 3; i++) {
      const spire = createMesh(GEOMETRY.titanSpire, spireMat);
      spire.position.set(0, 0, -7);
      spire.rotation.x = 0.52;
      spire.scale.set(0.85, 0.85, 0.85);
      root.add(spire);
      petals.push(spire);
    }
    materials.push(spireMat);

    root.userData = { petals, core };
    return { type: 'titan', detail: 'lite', group: root, materials, seed };
  }

  private createLiteOverlordVisual(seed: number): EnemyVisual {
    const root = new THREE.Group();
    const materials: LitMaterial[] = [];

    const thoraxMat = makeMaterial(0x60362a, 0xe25f46);
    varyMaterial(thoraxMat, (seed - 0.5) * 0.03, 0.02);
    const thorax = createMesh(GEOMETRY.overlordThorax, thoraxMat);
    thorax.rotation.z = Math.PI / 6;
    thorax.scale.set(1, 1, 0.66);
    root.add(thorax);
    materials.push(thoraxMat);

    const abdomenMat = makeMaterial(0x9d5c30, 0xff9f51);
    const abdomen = createMesh(GEOMETRY.overlordAbdomen, abdomenMat);
    abdomen.position.set(0, 15, -2.4);
    abdomen.scale.set(1.05, 1.28, 0.72);
    root.add(abdomen);
    materials.push(abdomenMat);

    const coreMat = makeMaterial(0xffd79e, 0xffba6d);
    const core = createMesh(GEOMETRY.overlordCore, coreMat);
    core.position.set(0, -1, 8.8);
    root.add(core);
    materials.push(coreMat);

    const wingMat = makeMaterial(0x6d4135, 0xc35f4e);
    const wings: THREE.Object3D[] = [];
    for (const side of [-1, 1] as const) {
      const wing = createMesh(GEOMETRY.overlordWing, wingMat);
      wing.position.set(side * 17, 0, -1.2);
      wing.rotation.z = side * 0.34;
      wing.rotation.x = 0.24;
      wing.scale.set(0.9, 0.9, 0.86);
      root.add(wing);
      wings.push(wing);
    }
    materials.push(wingMat);

    root.userData = { wings, core };
    return { type: 'overlord', detail: 'lite', group: root, materials, seed };
  }
}
